/**
 * useAutomationSettings Hook
 * Manage the tenant's own automation pause switch — stops scheduled campaigns,
 * behavior-triggered campaigns, and automation workflows from firing without
 * deleting or deactivating them. Independent of plan entitlement (mirrors
 * useRagVectorConfig.ts's GET/PATCH shape).
 */

import adminApi from '@/services/admin.api';
import { useEffect, useState } from 'react';

export const useAutomationSettings = () => {
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getAutomationSettings();
      setPaused(!!data.paused);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch automation settings');
    } finally {
      setLoading(false);
    }
  };

  const updatePaused = async (nextPaused: boolean) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const data = await adminApi.updateAutomationSettings(nextPaused);
      setPaused(!!data.paused);
      setSuccess(data.paused ? 'Automation paused' : 'Automation resumed');
    } catch (err: any) {
      setError(err.message || 'Failed to update automation settings');
    } finally {
      setSaving(false);
    }
  };

  return { paused, loading, saving, error, success, updatePaused, refetch: fetchSettings };
};
